/**
 * The pinned catalogue in the browser: loaded once, lazily (the snapshot is its own chunk), and shared
 * by every component through `useCatalog()`. Components render a placeholder until it arrives.
 */
import { type Catalog, loadPinnedCatalog } from '@play/catalog';
import { useSyncExternalStore } from 'react';

let current: Catalog | null = null;
let loading: Promise<Catalog> | null = null;
const listeners = new Set<() => void>();

/** Starts loading the catalogue (idempotent) and resolves with it. */
export function ensureCatalog(): Promise<Catalog> {
  loading ??= loadPinnedCatalog().then((catalog) => {
    current = catalog;
    for (const l of listeners) l();
    return catalog;
  });
  return loading;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  void ensureCatalog();
  return () => listeners.delete(listener);
}

/** The catalogue once loaded, `null` before. */
export function useCatalog(): Catalog | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  );
}

/** For tests: install a catalogue synchronously. */
export function setCatalogForTests(catalog: Catalog | null): void {
  current = catalog;
  loading = catalog ? Promise.resolve(catalog) : null;
  for (const l of listeners) l();
}
